import { useMemo, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import styled from "styled-components";
import { z } from "zod";
import { validatePassword, validatePhoneNumber } from "../../../utils/auth";
import { Button } from "../../atoms/Button";
import { Input } from "../../atoms/Input";
import FormTemplate from "../../templates/FormTemplate";

type Props = {
  name?: string;
  phoneNumber?: string;
  handleAuthValidationError: (
    isValidPhoneNumber: boolean,
    isValidEmail: boolean,
    isValidPassword: boolean,
  ) => void;
  handleSubmit: (
    name: string,
    phoneNumber: string,
    password: string,
    passwordConfirmation: string,
  ) => void;
};

export const CustomerInfoEditForm: React.FC<Props> = ({
  name,
  phoneNumber,
  handleAuthValidationError,
  handleSubmit,
}) => {
  const [hasPhoneNumberError, setHasPhoneNumberError] =
    useState<boolean>(false);
  const [hasPasswordError, setHasPasswordError] = useState<boolean>(false);

  const schema = useMemo(() => {
    return z
      .object({
        name: z.string().min(1),
        phoneNumber: z.string().min(1),
        password: z.string().min(1),
        passwordConfirmation: z.string().min(1),
      })
      .refine((data) => data.password === data.passwordConfirmation);
  }, []);

  const [methods, setMethods] = useState<UseFormReturn<
    FieldValues,
    any,
    undefined
  > | null>(null);

  return (
    <FormTemplate
      schema={schema}
      setMethods={setMethods}
      defaultValues={{
        name: name ?? "",
        phoneNumber: phoneNumber ?? "",
        password: "",
        passwordConfirmation: "",
      }}
      onSubmit={({ name, phoneNumber, password, passwordConfirmation }) => {
        setHasPhoneNumberError(false);
        setHasPasswordError(false);

        const isValidPhoneNumber = validatePhoneNumber(phoneNumber);
        const isValidPassword = validatePassword(password);

        if (isValidPhoneNumber && isValidPassword) {
          handleSubmit(name, phoneNumber, password, passwordConfirmation);
        } else {
          if (!isValidPhoneNumber) setHasPhoneNumberError(true);
          if (!isValidPassword) setHasPasswordError(true);

          handleAuthValidationError(isValidPhoneNumber, true, isValidPassword);
        }
      }}
    >
      <Message>※印は必須項目です。</Message>
      <ContentWrapper>
        <Input
          name="name"
          type="text"
          label="名前"
          labelBorderBottom
          required
          dataWovnIgnore={true}
        />
      </ContentWrapper>
      <ContentWrapper>
        <Input
          name="phoneNumber"
          type="text"
          label="電話番号"
          helperText="半角英数ハイフン無し"
          labelBorderBottom
          required
          hasError={hasPhoneNumberError}
          dataWovnIgnore={true}
        />
      </ContentWrapper>

      <TitleWrapper>パスワード</TitleWrapper>

      <ContentWrapper>
        <Input
          name="password"
          type="password"
          label="パスワード"
          helperText={`半角英数記号8文字以上24文字以内で、英字・数字・記号のうち2種類以上を混在させてください。\n
          使用可能な記号 ! " # $ % & ' ( ) * + , - . / : ; < = > ? @ [ \\ ] ^ _ \` { | } ~ \n
          ※スペースはご使用いただけません`}
          hasError={hasPasswordError}
          required
          dataWovnIgnore={true}
        />
      </ContentWrapper>
      <ContentWrapper>
        <Input
          name="passwordConfirmation"
          type="password"
          label="確認のためパスワードを再入力してください"
          hasError={hasPasswordError}
          required
          dataWovnIgnore={true}
        />
      </ContentWrapper>

      <ButtonWrapper>
        <Button btnStyle="primary" text="更新" type="submit" />
      </ButtonWrapper>
    </FormTemplate>
  );
};

const ContentWrapper = styled.div`
  margin: 0 0 30px;
`;

const Message = styled.div`
  text-align: left;
  margin-bottom: 26px;
  font-size: 14px;
  font-weight: 300;
  font-family: "Hiragino Sans";
`;

const TitleWrapper = styled.div`
  font-size: 20px;
  padding: 43px 0 38px;
  display: flex;
  font-weight: 500;
  justify-content: center;
  line-height: 1;
  font-family: "Hiragino Sans";
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 50px;
  button {
    line-height: 1;
    height: auto;
    font-weight: 500;
    font-family: "Hiragino Sans";
  }
`;
